import { type ReactNode } from "react";

import { parseCorrectedTextTokens } from "./correctedTextModel";

/**
 * Returns the corrected text with all markup resolved: replacements and
 * insertions keep their corrected form, deletions are dropped, plain text
 * passes through unchanged.
 */
export const buildPlainCorrectedText = (text: string): string => {
    let result = "";
    parseCorrectedTextTokens(text).forEach((token) => {
        if (token.type === "text") {
            result += token.text;
            return;
        }
        if (token.type === "replacement" || token.type === "insertion") {
            result += token.correct;
        }
    });
    return result.replace(/[ \t]+\n/g, "\n").replace(/[ \t]{2,}/g, " ").trim();
};

/**
 * Builds corrected text segments for writing/speaking feedback.
 * Text format: "I {have}[had] a car." -> "I <span class='line-through text-red-600'>have</span><span class='font-semibold text-green-700'>had</span> a car."
 */
export const buildCorrectedTextSegments = (text: string): ReactNode[] => {
    const segments: ReactNode[] = [];
    let key = 0;

    parseCorrectedTextTokens(text).forEach((token) => {
        if (token.type === "text") {
            segments.push(<span key={`text-${key++}`}>{token.text}</span>);
            return;
        }
        if (token.type === "replacement") {
            if (token.wrong) {
                segments.push(
                    <span key={`wrong-${key++}`} className="text-destructive line-through">
                        {token.wrong}
                    </span>,
                );
            }
            if (token.wrong && token.correct) {
                segments.push(<span key={`gap-${key++}`}> </span>);
            }
            if (token.correct) {
                segments.push(
                    <span key={`right-${key++}`} className="text-success font-semibold">
                        {token.correct}
                    </span>,
                );
            }
            return;
        }
        if (token.type === "deletion") {
            segments.push(
                <span key={`wrong-${key++}`} className="text-destructive line-through">
                    {token.wrong}
                </span>,
            );
            return;
        }
        segments.push(
            <span
                key={`right-${key++}`}
                className="inline-flex rounded bg-success/10 px-1 py-0.5 text-success font-semibold"
            >
                {token.correct}
            </span>,
        );
    });

    return segments;
};
